<script setup lang="ts">
    import { computed, toRef } from 'vue'

    import type { EvalModelClonerViewModel } from '@Evaluation/EvaluationModels/Components/EvalModelCloner'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        viewModel: EvalModelClonerViewModel
    }>()

    const emit = defineEmits<{
        (e: 'clone-evalmodel', sourceId: number, cloneName: string | null): void
    }>()

    // ---------------- shortcuts ----------------

    const vm = toRef(props, 'viewModel')

    // ---------------- derived ----------------

    const opened = computed(() => vm.value?.opened ?? false)

    const operationInProgress = computed(() => vm.value.operationInProgress)

    const cloneFailed = computed(() => vm.value.cloneFailed)

    const cloneError = computed<string | null>(() =>
        vm.value.errors.find(e => e.ErrorCode.startsWith('Export.'))?.Error ?? null
    )

    const goalError = computed<string | null>(() =>
        vm.value.errors.find(e => !e.ErrorCode.startsWith('Export.'))?.Error ?? null
    )

    // writable computed (mirrors getter/setter)
    const cloneName = computed<string | null>({
        get: () => vm.value.name,
        set: val => { vm.value.name = val }
    })

    // ---------------- actions ----------------

    function handleFormSubmit()
    {
        vm.value.operationInProgress = true
        vm.value.clearValidationErrors()

        emit('clone-evalmodel', vm.value.sourceId, cloneName.value)
    }

    function close()
    {
        vm.value.close()
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Clone</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>
                    <div class="modal-body">
                        <form id="evalmodel-cloner-form" @submit.prevent="handleFormSubmit">
                            <div class="alert alert-danger" v-if="cloneFailed">
                                <p>Clone failed. Sorry, something went wrong :(</p>
                                <small>{{cloneError}}</small>
                            </div>
                            <div class="form-group">
                                <label>Clone name:</label>
                                <input v-model.trim="cloneName" type="text" class="form-control">
                                <small class="text-danger">{{goalError}}</small>
                            </div>
                        </form>
                    </div>
                    <div class="modal-footer">
                        <button type="button" @click="close" class="btn btn-outline-secondary">Cancel</button>
                        <button type="submit" form="evalmodel-cloner-form" :disabled="operationInProgress" class="btn btn-primary">
                            <span v-if="operationInProgress"><i class="fa fa-spinner fa-spin"></i> Please wait...</span>
                            <span v-if="!operationInProgress">Clone</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>